const AUTO_WRAP_META_KEY = "autoWrapMeta";
const FONT_DPI_RATIO = 96 / 72;
const CELL_HORIZONTAL_PADDING = 4;
const MERGED_COLUMN_WIDTH_ADJUSTMENT = 1.25;
const INLINE_STYLE_KEYS = ["bl", "cl", "fc", "ff", "fs", "it", "un"];

function normalizeLineBreaks(value) {
	return value.replace(/\r\n?/g, "\n");
}

function getGeneratedBreaks(source, wrappedValue) {
	let sourceCharacters = Array.from(normalizeLineBreaks(source));
	let wrappedCharacters = Array.from(normalizeLineBreaks(wrappedValue));
	let sourceOffset = 0;
	let generatedBreaks = [];

	for (let valueOffset = 0; valueOffset < wrappedCharacters.length; valueOffset++) {
		let character = wrappedCharacters[valueOffset];
		if (character === "\n" && sourceCharacters[sourceOffset] !== "\n") {
			generatedBreaks.push({ sourceOffset: sourceOffset, valueOffset: valueOffset });
			continue;
		}

		if (character !== sourceCharacters[sourceOffset]) {
			return [];
		}
		sourceOffset++;
	}

	return sourceOffset === sourceCharacters.length ? generatedBreaks : [];
}

function getStoredAutoWrapSource(cell) {
	let meta = cell?.[AUTO_WRAP_META_KEY];
	if (meta == null || typeof meta.source !== "string") {
		return null;
	}

	let currentValue = typeof cell.v === "string" ? normalizeLineBreaks(cell.v) : null;
	let source = normalizeLineBreaks(meta.source);
	if (currentValue == null) {
		return null;
	}

	if (typeof meta.value === "string" && currentValue === normalizeLineBreaks(meta.value)) {
		return source;
	}

	if (Array.isArray(meta.generatedBreaks)) {
		let currentCharacters = Array.from(currentValue);
		let generatedOffsets = meta.generatedBreaks
			.map((item) => Number(item?.valueOffset))
			.filter((offset) => Number.isInteger(offset))
			.sort((a, b) => b - a);

		let validGeneratedBreaks = generatedOffsets.length === meta.generatedBreaks.length;
		for (let offset of generatedOffsets) {
			if (currentCharacters[offset] !== "\n") {
				validGeneratedBreaks = false;
				break;
			}
			currentCharacters.splice(offset, 1);
		}

		if (validGeneratedBreaks && currentCharacters.join("") === source) {
			return source;
		}
	}

	// Compatibility with metadata created before value/generatedBreaks were stored.
	if (typeof meta.signature === "string") {
		let [printableWidth, fontSize] = meta.signature.split("|").map(Number);
		if (
			Number.isFinite(printableWidth) &&
			Number.isFinite(fontSize) &&
			wrapTextByFixedWidth(source, printableWidth, fontSize) === currentValue
		) {
			return source;
		}
	}

	return null;
}

function getInlineStringStyleSignature(segment) {
	let style = {};
	Object.keys(segment || {})
		.filter((key) => key !== "v")
		.sort()
		.forEach((key) => {
			style[key] = segment[key];
		});
	return JSON.stringify(style);
}

function getUniformInlineStringContent(segments) {
	let lines = [{ text: "", signatures: new Map() }];
	let fallbackSegment = segments[0];

	segments.forEach((segment) => {
		let value = normalizeLineBreaks(segment?.v == null ? "" : String(segment.v));
		let pieces = value.split("\n");
		let signature = getInlineStringStyleSignature(segment);

		pieces.forEach((piece, index) => {
			let line = lines[lines.length - 1];
			line.text += piece;
			if (piece.length > 0) {
				line.signatures.set(signature, segment);
			}
			if (index < pieces.length - 1) {
				lines.push({ text: "", signatures: new Map() });
			}
		});
	});

	let uniformSignature = null;
	let styleSegment = fallbackSegment;
	for (let line of lines) {
		if (line.signatures.size > 1) {
			return null;
		}

		if (line.signatures.size === 1) {
			let [lineSignature, lineSegment] = line.signatures.entries().next().value;
			if (uniformSignature != null && lineSignature !== uniformSignature) {
				return null;
			}
			uniformSignature = lineSignature;
			styleSegment = lineSegment;
		}
	}

	return {
		styleSegment: styleSegment,
		value: lines.map((line) => line.text).join("\n"),
	};
}

function normalizeUniformInlineString(cell) {
	let segments = cell?.ct?.s;
	if (cell?.ct?.t !== "inlineStr" || !Array.isArray(segments) || segments.length === 0) {
		return false;
	}

	let content = getUniformInlineStringContent(segments);
	if (content == null) {
		return false;
	}

	INLINE_STYLE_KEYS.forEach((key) => {
		if (content.styleSegment[key] != null) {
			cell[key] = content.styleSegment[key];
		}
	});

	cell.v = content.value;
	cell.m = content.value;
	cell.ct = { ...cell.ct, t: "s" };
	delete cell.ct.s;
	return true;
}

function getAutoWrapEditSource(cell) {
	return getStoredAutoWrapSource(cell);
}

function isSongFont(fontFamily) {
	if (fontFamily == null) {
		return false;
	}

	if (Number(fontFamily) === 5) {
		return true;
	}

	let normalized = String(fontFamily).replace(/["']/g, "").trim().toLowerCase();
	return normalized === "宋体" || normalized === "simsun";
}

function normalizeFontFamily(fontFamily) {
	if (fontFamily == null) {
		return "";
	}

	return String(fontFamily).replace(/["']/g, "").trim().toLowerCase();
}

function isCombiningCodePoint(codePoint) {
	return (
		(codePoint >= 0x0300 && codePoint <= 0x036f) ||
		(codePoint >= 0x1ab0 && codePoint <= 0x1aff) ||
		(codePoint >= 0x1dc0 && codePoint <= 0x1dff) ||
		(codePoint >= 0x20d0 && codePoint <= 0x20ff) ||
		(codePoint >= 0xfe20 && codePoint <= 0xfe2f)
	);
}

function getCharacterWidthUnit(character) {
	let codePoint = character.codePointAt(0);
	if (isCombiningCodePoint(codePoint)) {
		return 0;
	}

	if (codePoint <= 0x007f || (codePoint >= 0xff61 && codePoint <= 0xffdc)) {
		return 0.5;
	}

	return 1;
}

function getSongFullWidthPixelSize(fontSize) {
	let size = Number(fontSize);
	if (!Number.isFinite(size) || size <= 0) {
		size = 10;
	}

	return Math.max(1, Math.round(size * FONT_DPI_RATIO));
}

function wrapTextByFixedWidth(source, printableWidth, fontSize) {
	let normalizedSource = normalizeLineBreaks(source);
	let fullWidthPixelSize = getSongFullWidthPixelSize(fontSize);
	let usableWidth = Math.max(0, Number(printableWidth) - CELL_HORIZONTAL_PADDING);
	let maxUnits = Math.max(0.5, usableWidth / fullWidthPixelSize);
	let wrappedLines = [];

	normalizedSource.split("\n").forEach((manualLine) => {
		if (manualLine.length === 0) {
			wrappedLines.push("");
			return;
		}

		let currentLine = "";
		let currentUnits = 0;
		for (let character of Array.from(manualLine)) {
			let characterUnits = getCharacterWidthUnit(character);
			if (currentLine.length > 0 && currentUnits + characterUnits > maxUnits) {
				wrappedLines.push(currentLine);
				currentLine = "";
				currentUnits = 0;
			}

			currentLine += character;
			currentUnits += characterUnits;
		}

		wrappedLines.push(currentLine);
	});

	return wrappedLines.join("\n");
}

function getColumnPixelWidth(config, defaultColWidth, columnIndex) {
	if (config?.colhidden?.[columnIndex] != null) {
		return 0;
	}

	let configuredWidth = config?.columnlen?.[columnIndex];
	let width = Number(configuredWidth);
	if (!Number.isFinite(width) || width < 0) {
		width = Number(defaultColWidth);
	}

	return Number.isFinite(width) && width >= 0 ? width : 0;
}

function getPrintableCellWidth(cell, rowIndex, columnIndex, config = {}, defaultColWidth = 0) {
	let merge = cell?.mc;
	if (merge?.cs == null) {
		merge = config?.merge?.[`${rowIndex}_${columnIndex}`];
	}

	let startColumn = merge?.c ?? (Number(columnIndex) || 0);
	let columnSpan = Math.max(1, Number(merge?.cs) || 1);
	let printableWidth = 0;

	for (let c = startColumn; c < startColumn + columnSpan; c++) {
		let columnWidth = getColumnPixelWidth(config, defaultColWidth, c);
		if (columnSpan > 1 && columnWidth > 0) {
			columnWidth = Math.max(0, columnWidth - MERGED_COLUMN_WIDTH_ADJUSTMENT);
		}
		printableWidth += columnWidth;
	}

	return printableWidth;
}

function restoreAutoWrappedCell(cell) {
	let meta = cell?.[AUTO_WRAP_META_KEY];
	if (meta == null) {
		return false;
	}

	let source = getStoredAutoWrapSource(cell);
	if (source != null) {
		cell.v = source;
		cell.m = source;
	}

	delete cell[AUTO_WRAP_META_KEY];
	return true;
}

function isFixedWrapEligible(cell, options) {
	return (
		cell != null &&
		cell.f == null &&
		cell.ct?.s == null &&
		String(options.tb) === "2" &&
		typeof cell.v === "string"
	);
}

function applyFixedWrapToCell(cell, options) {
	if (String(options.tb) === "2") {
		normalizeUniformInlineString(cell);
	}

	if (!isFixedWrapEligible(cell, options)) {
		restoreAutoWrappedCell(cell);
		return null;
	}
	if (!Number.isFinite(Number(options.printableWidth)) || Number(options.printableWidth) <= 0) {
		return null;
	}

	let meta = cell[AUTO_WRAP_META_KEY];
	let currentValue = normalizeLineBreaks(cell.v);
	let storedSource = getStoredAutoWrapSource(cell);
	let source = storedSource == null ? currentValue : storedSource;
	let fontSize = Number(options.fontSize) || 10;
	let printableWidth = Number(options.printableWidth) || 0;
	let fontFamily = normalizeFontFamily(options.fontFamily);
	let signature = [printableWidth, fontSize, fontFamily].join("|");

	if (meta != null && meta.signature === signature && storedSource != null) {
		return normalizeLineBreaks(meta.value).split("\n");
	}

	let wrappedValue = wrapTextByFixedWidth(source, printableWidth, fontSize);
	cell.v = wrappedValue;
	cell.m = wrappedValue;
	cell[AUTO_WRAP_META_KEY] = {
		generatedBreaks: getGeneratedBreaks(source, wrappedValue),
		source: source,
		signature: signature,
		value: wrappedValue,
	};

	return wrappedValue.split("\n");
}

function reflowFixedWrapCell(cell, options) {
	restoreAutoWrappedCell(cell);
	return applyFixedWrapToCell(cell, options);
}

function applyFixedWrapToSheet(sheet) {
	let data = sheet?.data;
	if (!Array.isArray(data)) {
		return;
	}

	let config = sheet.config || {};
	let defaultColWidth = sheet.defaultColWidth ?? 73;
	for (let r = 0; r < data.length; r++) {
		let row = data[r];
		if (!Array.isArray(row)) {
			continue;
		}

		for (let c = 0; c < row.length; c++) {
			let cell = row[c];
			if (cell == null || typeof cell !== "object") {
				continue;
			}

			if (String(cell.tb) === "2") {
				normalizeUniformInlineString(cell);
			}
			applyFixedWrapToCell(cell, {
				tb: cell.tb,
				fontFamily: cell.ff,
				fontSize: cell.fs,
				printableWidth: getPrintableCellWidth(cell, r, c, config, defaultColWidth),
			});
		}
	}
}

export {
	applyFixedWrapToCell,
	applyFixedWrapToSheet, AUTO_WRAP_META_KEY, getAutoWrapEditSource, getCharacterWidthUnit,
	getPrintableCellWidth,
	getSongFullWidthPixelSize, isSongFont,
	normalizeUniformInlineString,
	reflowFixedWrapCell,
	restoreAutoWrappedCell,
	wrapTextByFixedWidth
};

